import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchStockLogsAPI } from './stockLogsAPI';
import { StockLogEntry, StockLogPagination, StockLogFilters } from './types';

interface StockLogsState {
  logs: StockLogEntry[];
  pagination: StockLogPagination;
  filters: StockLogFilters;
  loading: boolean;
  error: string | null;
}

const initialState: StockLogsState = {
  logs: [],
  pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
  filters: { page: 1, limit: 20, sortBy: 'createdAt', sortOrder: 'desc' },
  loading: false,
  error: null,
};

export const fetchStockLogs = createAsyncThunk<
  { data: StockLogEntry[]; pagination: StockLogPagination },
  StockLogFilters | undefined,
  { rejectValue: string }
>('stockLogs/fetchStockLogs', async (filters = {}, { rejectWithValue }) => {
  try {
    const response = await fetchStockLogsAPI(filters);
    if (!response.status) {
      return rejectWithValue('Failed to fetch stock logs');
    }
    return { data: response.data, pagination: response.pagination };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch stock logs');
  }
});

const stockLogsSlice = createSlice({
  name: 'stockLogs',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<Partial<StockLogFilters>>) {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters(state) {
      state.filters = { page: 1, limit: 20, sortBy: 'createdAt', sortOrder: 'desc' };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStockLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStockLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.logs = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchStockLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch stock logs';
      });
  },
});

export const { setFilters, resetFilters } = stockLogsSlice.actions;
export default stockLogsSlice.reducer;
